import type { Cents } from "./money";
import { type Period, periodStartDate } from "./period";

export { versionInForce as selectedBoundaryVersion } from "./versions";
import { versionInForce as selectedBoundaryVersion } from "./versions";

/** Preserve the selected version when an earlier boundary would select another row. */
export function carriedBoundaryVersion<T extends { readonly effectiveFrom: string }>(
  versions: readonly T[], oldPeriod: Period, newPeriod: Period,
  oldTimezone: string, newTimezone: string,
): T | null {
  const current = selectedBoundaryVersion(versions, oldPeriod, oldTimezone);
  const next = selectedBoundaryVersion(versions, newPeriod, newTimezone);
  return periodStartDate(newPeriod, newTimezone) < periodStartDate(oldPeriod, oldTimezone)
    && current !== null && current !== next ? current : next;
}

/** The preview reads the same selected version that Save carries. */
export function carryAcrossBoundary<T extends { readonly effectiveFrom: string }>(
  versions: readonly T[], oldPeriod: Period, newPeriod: Period,
  oldTimezone: string, newTimezone: string,
  select: (rows: readonly T[], period: Period, timezone: string) => Cents | null,
): Cents | null {
  const selected = carriedBoundaryVersion(versions, oldPeriod, newPeriod, oldTimezone, newTimezone);
  const movedEarlier = periodStartDate(newPeriod, newTimezone) < periodStartDate(oldPeriod, oldTimezone);
  return select(selected ? [selected] : [], movedEarlier ? oldPeriod : newPeriod, movedEarlier ? oldTimezone : newTimezone);
}

/** Versions skipped by an earlier boundary, whose values are carried or explicitly replaced. */
export function redundantBoundaryVersions<T extends { readonly effectiveFrom: string }>(
  versions: readonly T[], effectiveFrom: string, oldStart: string,
): T[] {
  return versions.filter(row => row.effectiveFrom > effectiveFrom && row.effectiveFrom <= oldStart);
}
